import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays, Package, Plus } from 'lucide-react';
import { useInventaire, ArticleInventaire } from '@/hooks/useInventaire';
import { useBateaux } from '@/hooks/useBateaux';
import { useEspacesStockage } from '@/hooks/useEspacesStockage';
interface FormulaireAjoutArticleProps {
  onArticleAdded?: () => void;
  selectedBateauId?: string;
}

const FormulaireAjoutArticle = ({ onArticleAdded, selectedBateauId }: FormulaireAjoutArticleProps) => {
  const { categories, sousCategories, ajouterArticle, refetchArticles } = useInventaire();
  const { bateaux } = useBateaux();
  const { espaces } = useEspacesStockage();
  
  const [formData, setFormData] = useState({
    bateau_id: selectedBateauId || '',
    nom: '',
    description: '',
    notes: '',
    categorie: '',
    sous_categorie: '',
    date_achat: '',
    date_peremption: '',
    quantite: 0,
    quantite_max: 1,
    unite: 'unité',
    prix_unitaire: 0,
    espace_stockage_id: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mettre à jour le bateau sélectionné quand il change
  useEffect(() => {
    if (selectedBateauId) {
      console.log('FormAjout: selectedBateauId changed to:', selectedBateauId);
      setFormData(prev => ({ ...prev, bateau_id: selectedBateauId }));
    }
  }, [selectedBateauId]);

  const sousCategoriesToDisplay = sousCategories.filter(sc => {
    const categorie = categories.find(c => c.nom === formData.categorie);
    return categorie && sc.categorie_id === categorie.id;
  });

  const espacesForSelectedBoat = espaces.filter(e => e.bateau_id === formData.bateau_id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bateau_id || !formData.nom || !formData.categorie) {
      console.log('Form validation failed:', formData);
      return;
    }

    setIsSubmitting(true);
    console.log('Submitting article with data:', formData);
    
    const result = await ajouterArticle({
      bateau_id: formData.bateau_id,
      nom: formData.nom,
      description: formData.description || null,
      notes: formData.notes || null,
      categorie: formData.categorie,
      sous_categorie: formData.sous_categorie || null,
      date_achat: formData.date_achat || null,
      date_peremption: formData.date_peremption || null,
      quantite: formData.quantite,
      quantite_max: formData.quantite_max,
      unite: formData.unite,
      prix_unitaire: formData.prix_unitaire || null,
      espace_stockage_id: formData.espace_stockage_id || null,
    });

    if (!result.error) {
      setFormData({
        bateau_id: selectedBateauId || '',
        nom: '',
        description: '',
        notes: '',
        categorie: '',
        sous_categorie: '',
        date_achat: '',
        date_peremption: '',
        quantite: 0,
        quantite_max: 1,
        unite: 'unité',
        prix_unitaire: 0,
        espace_stockage_id: '',
      });
      // Rafraîchir l'inventaire après ajout
      if (selectedBateauId) {
        await refetchArticles(selectedBateauId);
      }
      onArticleAdded?.();
    }
    setIsSubmitting(false);
  };

  const handleBateauChange = (value: string) => {
    console.log('Form bateau selection changed to:', value);
    setFormData(prev => ({ ...prev, bateau_id: value, espace_stockage_id: '' }));
  };

  const handleSousCategorieChange = (value: string) => {
    // Si la valeur est "no-subcategory", on met une chaîne vide
    setFormData(prev => ({ 
      ...prev, 
      sous_categorie: value === "no-subcategory" ? "" : value 
    }));
  };

  const handleEspaceChange = (value: string) => {
    setFormData(prev => ({ 
      ...prev, 
      espace_stockage_id: value === "no-space" ? "" : value 
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Ajouter un article
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bateau">Navire *</Label>
              <Select
                value={formData.bateau_id || ""}
                onValueChange={handleBateauChange}
                disabled={!!selectedBateauId && bateaux.length > 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un navire" />
                </SelectTrigger>
                <SelectContent>
                  {bateaux.map((bateau) => (
                    <SelectItem key={bateau.id} value={bateau.id}>
                      {bateau.nom} - {bateau.nom_constructeur} {bateau.nom_modele}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Entrer le nom de l'article"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de l'article"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes supplémentaires (emplacement précis, remarques, etc.)"
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categorie">Catégorie *</Label>
              <Select
                value={formData.categorie || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, categorie: value, sous_categorie: '' }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((categorie) => (
                    <SelectItem key={categorie.id} value={categorie.nom}>
                      {categorie.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sous_categorie">Sous-catégorie</Label>
              <Select
                value={formData.sous_categorie || "no-subcategory"}
                onValueChange={handleSousCategorieChange}
                disabled={!formData.categorie || sousCategoriesToDisplay.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une sous-catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-subcategory">Aucune sous-catégorie</SelectItem>
                  {sousCategoriesToDisplay.map((sousCategorie) => (
                    <SelectItem key={sousCategorie.id} value={sousCategorie.nom}>
                      {sousCategorie.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="espace_stockage">Espace de stockage</Label>
              <Select
                value={formData.espace_stockage_id || "no-space"}
                onValueChange={handleEspaceChange}
                disabled={!formData.bateau_id || espacesForSelectedBoat.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un espace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-space">Aucun espace spécifique</SelectItem>
                  {espacesForSelectedBoat.map((espace) => (
                    <SelectItem key={espace.id} value={espace.id}>
                      {espace.nom} ({espace.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_achat">Date d'achat</Label>
              <Input
                id="date_achat"
                type="date"
                value={formData.date_achat}
                onChange={(e) => setFormData(prev => ({ ...prev, date_achat: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_peremption">Date de péremption</Label>
              <Input
                id="date_peremption"
                type="date"
                value={formData.date_peremption}
                onChange={(e) => setFormData(prev => ({ ...prev, date_peremption: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantite">Quantité actuelle *</Label>
              <Input
                id="quantite"
                type="number"
                min="0"
                value={formData.quantite}
                onChange={(e) => setFormData(prev => ({ ...prev, quantite: parseInt(e.target.value) || 0 }))}
                placeholder="0"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantite_max">Quantité maximale *</Label>
              <Input
                id="quantite_max"
                type="number"
                min="1"
                value={formData.quantite_max}
                onChange={(e) => setFormData(prev => ({ ...prev, quantite_max: parseInt(e.target.value) || 1 }))}
                placeholder="1"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unite">Unité de mesure</Label>
              <Select
                value={formData.unite}
                onValueChange={(value) => setFormData(prev => ({ ...prev, unite: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une unité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unité">unité</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="g">g</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="mL">mL</SelectItem>
                  <SelectItem value="boîte">boîte</SelectItem>
                  <SelectItem value="pack">pack</SelectItem>
                  <SelectItem value="sachet">sachet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prix_unitaire">Prix unitaire (€)</Label>
              <Input
                id="prix_unitaire"
                type="number"
                step="0.01"
                min="0"
                value={formData.prix_unitaire}
                onChange={(e) => setFormData(prev => ({ ...prev, prix_unitaire: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
              />
            </div>
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold">
            {isSubmitting ? 'Ajout en cours...' : 'Ajouter l\'article'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default FormulaireAjoutArticle;
